/**
 * SAYM newsletter pop-up.
 *
 * Opens a few seconds after the page loads, unless the visitor closed it recently
 * or has already signed up. That memory lives in localStorage, so it is per browser.
 * After the form posts, Shopify reloads the page and the markup arrives in the
 * thank-you (or error) state: the pop-up then opens straight away instead.
 *
 * It never opens on top of another open dialog (cart, search, quick add); it waits
 * for that to close first.
 */

import { lockScroll, unlockScroll } from '@theme/utilities';

const STORAGE_KEY = 'saym:newsletter-popup';
const DAY = 24 * 60 * 60 * 1000;
const RETRY = 1500;

const memory = {
  /** @returns {{ state?: string, at?: number }} */
  read() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {};
    } catch {
      return {};
    }
  },

  /** @param {'dismissed' | 'subscribed'} state */
  write(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ state, at: Date.now() }));
    } catch {
      // Blocked storage: the pop-up simply comes back on the next page view.
    }
  },
};

class SaymNewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.dialog.addEventListener('close', this.#onClose);
    this.dialog.addEventListener('click', this.#onClick);
    document.addEventListener('click', this.#onTrigger);

    const state = this.querySelector('[data-popup-state]')?.getAttribute('data-popup-state');
    if (state === 'success') {
      memory.write('subscribed');
      this.#open();
      return;
    }
    if (state === 'error') {
      this.#open();
      return;
    }

    // The theme editor would otherwise interrupt every edit.
    if (this.hasAttribute('data-design-mode')) return;
    if (!this.#due()) return;

    const delay = Math.max(0, Number(this.dataset.delay) || 3) * 1000;
    this.timer = setTimeout(() => this.#openWhenFree(), delay);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.dialog?.removeEventListener('close', this.#onClose);
    this.dialog?.removeEventListener('click', this.#onClick);
    document.removeEventListener('click', this.#onTrigger);
    if (this.dialog) unlockScroll(this.dialog);
  }

  /**
   * Links to #newsletter (announcement bar, product page teaser) open the pop-up on
   * demand. Where the pop-up is not rendered, the same link scrolls to the footer form.
   */
  #onTrigger = (/** @type {MouseEvent} */ event) => {
    const link = /** @type {Element} */ (event.target).closest?.('a[href$="#newsletter"], [data-saym-newsletter]');
    if (!link || this.dialog?.contains(link)) return;
    event.preventDefault();
    clearTimeout(this.timer);
    this.#open();
  };

  /** Whether enough time has passed since the visitor last closed it. */
  #due() {
    const { state, at } = memory.read();
    if (state === 'subscribed') return false;
    if (state === 'dismissed' && at) {
      const days = Number(this.dataset.days) || 14;
      return Date.now() - at > days * DAY;
    }
    return true;
  }

  #openWhenFree() {
    const busy = [...document.querySelectorAll('dialog[open]')].some((open) => open !== this.dialog);
    if (busy || document.hidden) {
      this.timer = setTimeout(() => this.#openWhenFree(), RETRY);
      return;
    }
    this.#open();
  }

  #open() {
    if (!this.dialog || this.dialog.open) return;
    this.dialog.showModal();
    // Focus the panel, not its first button: no focus ring on the close button,
    // and no keyboard jumping up on phones. Tab still moves into the form.
    this.dialog.focus();
    lockScroll(this.dialog);
  }

  #onClose = () => {
    if (this.dialog) unlockScroll(this.dialog);
    if (memory.read().state !== 'subscribed') memory.write('dismissed');
  };

  #onClick = (/** @type {MouseEvent} */ event) => {
    const target = /** @type {Element} */ (event.target);

    // A click on the dialog itself is a click on the backdrop around the panel.
    if (target === this.dialog || target.closest('[data-popup-close]')) {
      this.dialog?.close();
      return;
    }

    const copy = target.closest('[data-popup-copy]');
    if (copy instanceof HTMLElement) this.#copy(copy);
  };

  /** @param {HTMLElement} button */
  async #copy(button) {
    const code = button.dataset.popupCopy || '';
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // No clipboard access: select the code so it can be copied by hand.
      const value = this.querySelector('.saym-popup__code-value');
      if (value) window.getSelection()?.selectAllChildren(value);
      return;
    }
    const label = button.textContent;
    button.textContent = button.dataset.labelCopied || label;
    setTimeout(() => (button.textContent = label), 2000);
  }
}

if (!customElements.get('saym-newsletter-popup')) customElements.define('saym-newsletter-popup', SaymNewsletterPopup);
